import type { KeenSliderOptions } from 'keen-slider'
import 'keen-slider/keen-slider.min.css'
import { useKeenSlider } from 'keen-slider/react'
import type { FunctionComponent, LegacyRef, ReactElement } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { KeenSliderOptionTypes } from '../hooks/useResponsiveStackGridOptions'
import { useResponsiveKeenSliderOptions } from '../hooks/useResponsiveStackGridOptions'
import { GridWithGaps } from '../layout/MainGrid'
import { AccentroSlides } from './AccentroSlide'
import { SliderBottomNavigation } from './SliderBottomNavigation'
import { SliderBottomNavigationWithPreview } from './SliderBottomNavigationWithPreview'
import { SliderChevron } from './SliderChevrons'

const bottomNavigationWrapperClasses = (optionType: KeenSliderOptionTypes) => {
  const base = [
    'z-10',
    'flex',
    'items-center',
    'col-span-12',
    'mt-px-32',
    'ml:mt-px-48',
  ]
  if (optionType === 'MoreFlats') base.push('ml:mt-px-32')
  if (optionType === 'RecommendedFlats')
    base.push(
      'mt-px-64',
      'mr-px-24',
      'md:margin-top-[88px]',
      'md:mr-px-48',
      'ml:margin-right-[91px]'
    )
  if (optionType === 'PropImageSlider') base.push('mt-px-32', 'ml:mt-px-24')
  if (
    optionType === 'CurrentPressArticles' ||
    optionType === 'WorkingForAccentro'
  )
    base.push('mt-px-40', 'md:col-span-6', 'md:mt-px-48', 'lg:col-span-5')
  return base.join(' ')
}

const accentroSliderWrapperClasses = () => 'col-span-full relative'

const sliderChevronWrapperClasses = (optionType: KeenSliderOptionTypes) => {
  return [
    'flex',
    'justify-end',
    optionType !== 'PropImageSlider' ? 'ml-px-32 md:ml-px-48' : null,
  ]
    .filter(Boolean)
    .join(' ')
}

const wrapperClasses = (optionType: KeenSliderOptionTypes) =>
  [optionType === 'PropImageSlider' ? 'w-[75%] m-auto' : null]
    .filter(Boolean)
    .join(' ')

export type AccentroSliderProps = {
  slides: Slide[]
  thumbnails?: Slide[]
  videos?: Slide[]
  hasVideo?: boolean
  width?: number
  onClick?: () => void
  currentSlide?: number
  optionType: KeenSliderOptionTypes
  slidesPerView?: number
  spacing?: number
  withSelection?: boolean
  useBottomNavigation?: boolean
  onSlideChange?: (slide: number) => void
}

export type SlideType = 'picture' | 'video' | 'lexicon'

export type Slide = {
  title?: string | null
  slide: ReactElement
  type?: SlideType
  active?: boolean
  hasContent?: boolean
}

export const AccentroSlider: FunctionComponent<
  React.PropsWithChildren<AccentroSliderProps>
> = ({
  slides,
  videos,
  width,
  onClick,
  currentSlide = 0,
  useBottomNavigation,
  optionType,
  thumbnails,
}) => {
  const [slideIndex, setSlideIndex] = useState(currentSlide)

  const [pause, setPause] = useState(false)
  const hasVideo = !!videos
  const timer = useRef<ReturnType<typeof setInterval> | null>(null)
  const {
    slidesPerPage,
    spacing,
    height,
    duration,
    autoSlide,
    freezeDuration,
  } = useResponsiveKeenSliderOptions(optionType)
  const sliderOptions = useMemo(() => {
    return {
      initial: currentSlide,
      mode: 'snap',
      rtl: false,
      loop: !!autoSlide,
      slides: {
        perView: slidesPerPage,
        spacing: spacing,
      },
      duration: duration || 1000,
      freezeDuration: freezeDuration || 5000,
      slideChanged: (slider) => {
        setSlideIndex(slider.track.details.rel)
      },
    } as KeenSliderOptions & { freezeDuration?: number }
  }, [
    currentSlide,
    autoSlide,
    slidesPerPage,
    spacing,
    duration,
    freezeDuration,
  ])

  const [sliderRef, slider] = useKeenSlider(sliderOptions)
  const slideDuration = sliderOptions.freezeDuration

  useEffect(() => {
    slider?.current?.moveToIdx(currentSlide)
  }, [currentSlide, slider])

  const sliderSize =
    slider && slider.current ? slider.current.track.details?.slides.length : 0
  const notEnoughSlides = sliderSize <= Math.round(slidesPerPage)

  useEffect(() => {
    if (autoSlide) {
      slider.current?.container.addEventListener('mouseover', () => {
        setPause(true)
      })
      slider.current?.container.addEventListener('mouseout', () => {
        setPause(false)
      })
    }
  }, [slider, autoSlide])

  useEffect(() => {
    slider.current?.update()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides?.length])

  useEffect(() => {
    if (autoSlide) {
      timer.current = setInterval(() => {
        if (!pause && slider) {
          slider.current?.next()
        }
      }, slideDuration)
      return () => {
        if (timer.current) clearInterval(timer.current)
      }
    }
  }, [pause, slider, slideDuration, autoSlide])

  return (
    <div className={wrapperClasses(optionType)}>
      <GridWithGaps>
        <div className={accentroSliderWrapperClasses()}>
          <div className="w-full">
            <div
              ref={sliderRef as LegacyRef<HTMLDivElement>}
              className="keen-slider"
            >
              {hasVideo && <AccentroSlides slides={videos} />}

              <AccentroSlides
                height={height}
                width={width}
                slides={slides}
                onClick={onClick}
              />
            </div>
          </div>
          {optionType === 'PropImageSlider' && !!slider && (
            <>
              <SliderChevron
                slider={slider}
                disabled={!autoSlide && slideIndex === 0}
                isLeft
                optionType={optionType}
              />
              <SliderChevron
                optionType={optionType}
                slider={slider}
                disabled={
                  !autoSlide &&
                  slideIndex === Math.ceil(sliderSize - slidesPerPage)
                }
              />
            </>
          )}
        </div>
        {!notEnoughSlides &&
          (optionType !== 'PropImageSlider' ? (
            <div className={bottomNavigationWrapperClasses(optionType)}>
              <div className="w-full">
                {useBottomNavigation && (
                  <SliderBottomNavigation
                    optionType={optionType}
                    slideIndex={slideIndex}
                    slider={slider}
                    slidesPerView={slidesPerPage}
                  />
                )}
              </div>
              <div className={sliderChevronWrapperClasses(optionType)}>
                <SliderChevron
                  slider={slider}
                  disabled={!autoSlide && slideIndex === 0}
                  isLeft
                  optionType={optionType}
                />
                <SliderChevron
                  optionType={optionType}
                  slider={slider}
                  disabled={
                    !autoSlide &&
                    slideIndex === Math.ceil(sliderSize - slidesPerPage)
                  }
                />
              </div>
            </div>
          ) : (
            <div className={bottomNavigationWrapperClasses(optionType)}>
              <div className="w-full flex justify-center">
                {useBottomNavigation && (
                  <SliderBottomNavigationWithPreview
                    thumbnails={thumbnails}
                    optionType={optionType}
                    slideIndex={slideIndex}
                    slider={slider}
                    slidesPerView={slidesPerPage}
                  />
                )}
              </div>
            </div>
          ))}
      </GridWithGaps>
    </div>
  )
}
